import {
  ExpandedState,
  OnChangeFn,
  TableState,
  Updater,
} from "@tanstack/react-table";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useEventCallback, useSessionStorage } from "usehooks-ts";
import { z } from "zod";

import { isDeepEqual } from "@punto/frontend-shared/utils/is-equal";
import {
  clearSessionValuesWithPrefix,
  readSessionValue,
  writeSessionValue,
} from "@punto/frontend-shared/utils/session-storage";

import {
  type SearchParamsStateChangeOptions,
  applyStateToSearchParams,
  useSearchParamsState,
} from "./use-search-params-state";

// Base interface for table state - must have pagination, sorting, and filters
export interface BaseTableState<TFilters = Record<string, unknown>> {
  pagination: TableState["pagination"];
  sorting: TableState["sorting"];
  filters?: TFilters;
  /**
   * Ids of the expanded rows, for a table whose rows expand — the grouped
   * procedure views (P-2787). Optional because a table that never expands
   * anything (every Portus list) has no reason to declare it; a schema opts in.
   *
   * An array rather than TanStack's own `ExpandedState`: see
   * {@link expandedIdsToRecord} for why the record shape cannot be what we
   * store.
   */
  expanded?: string[];
}

// Configuration for the table state hook
export interface TableStateConfig<
  TTableState extends BaseTableState<TFilters>,
  TFilters,
> {
  defaultState: TTableState;
  schema: z.ZodSchema<TTableState>;
  storageKey: string;
  /**
   * Whether `defaultState` and `storageKey` are **settled**. Both can be derived
   * from async data — a list view's preset and identity arrive with a fetch — and
   * until they do they describe a different table than the URL asks for.
   *
   * While false the hook still reads, so the table renders, but makes none of its
   * own writes: it neither reflects state into `?state=` nor seeds session storage
   * from it. Writing against a provisional default is what let a cleared filter
   * come back after a reload — the state was persisted under, and compared
   * against, a default that was about to change.
   *
   * Defaults to true: most tables have a constant default state and no such window.
   */
  isReady?: boolean;
}

/**
 * Every table's session entry lives under one namespace so the whole set can be
 * dropped at once — see {@link clearStoredTableState}. Callers keep passing their
 * plain `storageKey`; the prefix is applied here.
 */
export const TABLE_STATE_STORAGE_PREFIX = "table-state:";

/**
 * The actual sessionStorage key for a `storageKey`. Prefer
 * {@link readStoredTableState}/{@link writeStoredTableState} over building the
 * key and touching `sessionStorage` yourself — this is exported for tests that
 * assert on the stored entry directly.
 */
export const tableStateStorageKey = (storageKey: string): string =>
  `${TABLE_STATE_STORAGE_PREFIX}${storageKey}`;

/**
 * Read/write a table's stored state **by its plain `storageKey`** — the same
 * value passed to `useTableState`, never the namespaced one.
 *
 * These are the storage primitives, not the way an application applies a filter:
 * a write that comes with a navigation goes through {@link useApplyTableState},
 * which writes this sink *and* the URL one (see its doc for why either alone is
 * silently wrong). What is left for the raw reader is code that needs a list's
 * filter set without going there — the ESC sibling navigation.
 *
 * Hand-building the key instead is silently wrong the moment the namespace
 * changes — `sessionStorage.getItem("x-table-state")` just returns `null`, which
 * is how that same ESC navigation ended up computing prev/next over the
 * *unfiltered* certificate set (P-1585).
 */
// The trailing comma in `<T,>` is required: this is a .tsx file, so a bare `<T>`
// parses as the start of a JSX element.
export const readStoredTableState = <T,>(
  storageKey: string,
  schema: z.ZodSchema<T>,
): T | undefined => readSessionValue(tableStateStorageKey(storageKey), schema);

export const writeStoredTableState = <T,>(storageKey: string, value: T): void =>
  writeSessionValue(tableStateStorageKey(storageKey), value);

/**
 * Drop every stored table state. Call it when the identity the filters were
 * built against stops being valid — switching organization, where a stored
 * customer id belongs to customers the user can no longer see, so restoring it
 * would filter the list down to nothing the user can explain. NOT for ordinary
 * navigation, where restoring is the whole point.
 */
export const clearStoredTableState = (): void =>
  clearSessionValuesWithPrefix(TABLE_STATE_STORAGE_PREFIX);

/**
 * Where an applied state is going: the list's route, the plain `storageKey` its
 * table passes to `useTableState`, and that table's `defaultState` — which is
 * what "nothing applied" means *there*, and so decides whether the state is
 * serialized into the URL or dropped from it.
 *
 * All three describe the TARGET list rather than the caller, because an applier
 * generally runs from somewhere else entirely — a sidebar search box, a redirect
 * screen — and often under a different storage key than the one it writes.
 */
export interface TableStateTarget<TTableState> {
  pathname: string;
  storageKey: string;
  defaultState: TTableState;
}

export interface ApplyTableStateOptions {
  /**
   * Amend the current history entry instead of adding one. For a refinement of
   * something already applied, where a new entry would give Back a step the user
   * does not think of as one.
   */
  replace?: boolean;
}

/**
 * Apply a complete table state to a list and navigate there.
 *
 * Table state has exactly TWO writers, both in this module, and between them they
 * are the only ones anywhere: the setters {@link useTableState} returns, which
 * edit the table you are **on**, and this one, which hands a state to a list you
 * are **going to**. Anything that changes, adds, edits or clears filters goes
 * through one of them — never `writeStoredTableState` plus a hand-built `?state=`
 * at the call site, which is how the two sinks drifted apart in the first place
 * (P-1585: clearing the Portus sidebar's global search emptied the box over a
 * list that stayed filtered).
 *
 * Both sinks, every time, because either alone is silently wrong:
 *
 * - **URL only** — a state that equals the target's default is serialized by
 *   *deleting* `?state=`, and an absent `?state=` is also how "nothing applied,
 *   restore what was stored" reads. The stale stored state then wins on arrival.
 *   Applying a *cleared* state is exactly that case, which is why clearing was
 *   the operation that broke while typing appeared to work.
 * - **Storage only** — a `?state=` already in the URL outranks storage, so the
 *   write would be ignored for as long as it stays there.
 *
 * Params this layer does not own ride along untouched (Portus's `?customer=`
 * scope): the next params are built off the live ones, not from scratch.
 */
export const useApplyTableState = <TTableState,>(
  target: TableStateTarget<TTableState>,
) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Identity-stable, while still reading the current params and target on every
  // call: callers hold it inside a debouncer (the Portus global search) where a
  // fresh callback per render would rebuild the timer and drop the pending call.
  return useEventCallback(
    (state: TTableState, options?: ApplyTableStateOptions) => {
      writeStoredTableState(target.storageKey, state);

      const search = applyStateToSearchParams(
        searchParams,
        state,
        target.defaultState,
      ).toString();

      // A `To` object, so react-router assembles the location — no query string
      // concatenated by hand, and an empty `search` correctly yields no `?` at all.
      void navigate(
        { pathname: target.pathname, search },
        { replace: options?.replace },
      );
    },
  );
};

/**
 * Stored ids → the record `useReactTable` wants for its `expanded` state.
 *
 * Expansion is stored as an array because the array is the only shape that can
 * deep-equal the empty default once everything is collapsed. A record keeps
 * collapsed rows around as `{id: false}`, and `{a: false}` never equals `{}` —
 * so `applyStateToSearchParams` would never delete `?state=` again, and a
 * fully-collapsed list would carry a `?state=` recording that nothing is open.
 */
const expandedIdsToRecord = (ids: string[] = []): ExpandedState =>
  Object.fromEntries(ids.map((id) => [id, true]));

/**
 * ...and back. Returns `undefined` for a state this representation cannot hold,
 * which the caller treats as "make no change".
 */
const expandedRecordToIds = (value: ExpandedState): string[] | undefined => {
  // `true` is TanStack's "every row, including ones not loaded" — there is no
  // set of ids that means it. No consumer can produce it today: `DataTable`
  // offers no expand-all control and only ever calls `row.toggleExpanded()`.
  // Declining the write leaves the open groups alone, which is a visible no-op;
  // mapping it to `[]` would silently collapse them instead.
  if (value === true) return undefined;

  return Object.entries(value)
    .filter(([, isExpanded]) => isExpanded)
    .map(([id]) => id);
};

// Return type for the hook
export interface TableStateReturn<TFilters> {
  // Current state
  pagination: TableState["pagination"];
  sorting: TableState["sorting"];
  filters?: TFilters;
  /** Ready to hand to `DataTable`'s `expanded` prop. `{}` when nothing is open. */
  expanded: ExpandedState;

  // State setters
  setPagination: (updater: Updater<TableState["pagination"]>) => void;
  setSorting: (updater: Updater<TableState["sorting"]>) => void;
  setExpanded: OnChangeFn<ExpandedState>;
  onFiltersChange: (updatedFilters: Partial<TFilters>) => void;
  onFiltersClear: () => void;

  // Utilities
  getPageCount: (total?: number) => number;
}

/**
 * Hook that manages common table state patterns:
 * - Pagination, sorting, and filter management
 * - Session storage persistence — what carries state across navigation
 * - `?state=` in the URL, written **only when the user applies something**: it is
 *   a record of their action, not a mirror of what the table holds, so merely
 *   moving around never puts filters in the URL or in the history
 * - Consistent state update patterns
 *
 * Usage:
 * ```typescript
 * const tableState = useTableState({
 *   defaultState: myDefaultState,
 *   schema: MyTableStateSchema,
 *   storageKey: 'my-table-state'
 * });
 * ```
 */
export const useTableState = <
  TTableState extends BaseTableState<TFilters>,
  TFilters = TTableState["filters"],
>(
  config: TableStateConfig<TTableState, TFilters>,
): TableStateReturn<TFilters> => {
  const { defaultState, schema, storageKey, isReady = true } = config;

  // Initial state from URL params
  const [initialState] = useSearchParamsState<TTableState>(
    defaultState,
    schema,
    { defaultState },
  );

  // A populated `?state=` in the URL is an explicit deep link (e.g. the CAE
  // batch-filter chip). It must win over any stale session-stored state —
  // otherwise `useSessionStorage` returns the previously stored value for a
  // returning user and the linked filters are silently ignored.
  const [searchParams, setSearchParams] = useSearchParams();
  const searchString = searchParams.toString();
  const hasUrlState = searchParams.has("state");

  // Session storage state
  const sessionKey = tableStateStorageKey(storageKey);
  const [storedState, setStoredState] = useSessionStorage(
    sessionKey,
    initialState,
  );

  /**
   * `useSessionStorage` re-reads on a key change in an **effect**, so for one
   * render after `storageKey` changes its value still belongs to the previous key
   * — and a view switch changes the key without remounting. That lag is what let
   * the previous view's filters leak into the new view's first request and URL.
   *
   * Reading synchronously for the current key removes the lag; `storedState` stays
   * in the deps purely as the write signal, since a `setStoredState` re-render is
   * what should invalidate this read.
   */
  const storedForKey = useMemo(
    () => readSessionValue(sessionKey, schema) ?? initialState,
    // eslint-disable-next-line react-hooks/exhaustive-deps -- storedState is the invalidation signal for the synchronous read above, not an input
    [sessionKey, schema, initialState, storedState],
  );

  // Content-stabilized: the read above parses fresh JSON, so its identity changes
  // every render, and this feeds the memo chain behind `state`.
  const storedForKeyRef = useRef(storedForKey);
  if (!isDeepEqual(storedForKeyRef.current, storedForKey)) {
    storedForKeyRef.current = storedForKey;
  }

  // What the CURRENT LOCATION says the state is. When the URL carries an explicit
  // state we seed from it (URL wins); otherwise we restore from session storage,
  // which is what carries filters across navigation.
  const locationState = hasUrlState ? initialState : storedForKeyRef.current;

  /**
   * The URL write this hook is still waiting on, and the state that goes with it.
   *
   * `setTableState` writes both sinks at once, but only one of them lands
   * synchronously: `setStoredState` is a state update, while the URL write is a
   * router navigation that commits a tick later. For that tick the location still
   * describes the state we just replaced — and every decision here is made
   * against the location, so a second write in the same tick is computed against
   * a query string that is already wrong.
   *
   * That is not merely a stale render. Serialization deletes `?state=` for a state
   * equal to `defaultState`, so *clearing* the filters while the previous write is
   * in flight produces a target identical to the not-yet-updated URL, the write is
   * skipped as a no-op, and the in-flight navigation then lands and reinstates the
   * filters the user just cleared — permanently, since nothing else ever removes
   * `?state=` (P-2731). The seeding effect below would compound it by copying that
   * stale URL back over the freshly cleared session entry.
   *
   * So while a write is in flight it, not the location, is the truth: reads come
   * from `state`, the next write is computed against `to`, and the seeding effect
   * stands down. `from` is what makes the handover unambiguous — the URL passing
   * through it means the write is still on its way, and the URL becoming anything
   * else (it reached `to`, or the user pressed Back) means the location is
   * authoritative again.
   */
  const pendingUrlWriteRef = useRef<
    { from: string; to: string; state: TTableState } | undefined
  >(undefined);

  // Reconciled on an *observed* change of the query string rather than on its
  // current value: a write whose target happens to equal the live URL (clearing,
  // again) would otherwise look landed the moment it was issued.
  const seenSearchRef = useRef(searchString);
  if (seenSearchRef.current !== searchString) {
    seenSearchRef.current = searchString;

    if (
      pendingUrlWriteRef.current &&
      searchString !== pendingUrlWriteRef.current.from
    ) {
      pendingUrlWriteRef.current = undefined;
    }
  }

  const state = pendingUrlWriteRef.current?.state ?? locationState;

  /**
   * The URL sink. Serialization stays in `applyStateToSearchParams` — this only
   * decides what to serialize *against*, which is the whole point: `effectiveSearch`
   * rather than the live params, so consecutive writes chain instead of the second
   * one measuring itself against a URL the first has already superseded.
   *
   * Keyed on the live query string rather than made identity-stable, because the
   * reflecting effect below depends on it and has to re-decide whenever the URL
   * changes: a path change that drops `?state=` is exactly when a still-applied
   * state needs putting back.
   */
  const writeUrlState = useCallback(
    (nextState: TTableState, options?: SearchParamsStateChangeOptions) => {
      // Read from the ref rather than from a value derived during render: two
      // writes can be issued from the SAME render's closures — the caller's, and
      // the reflecting effect below — and the second has to see the first.
      const effectiveSearch = pendingUrlWriteRef.current?.to ?? searchString;

      const nextParams = applyStateToSearchParams(
        new URLSearchParams(effectiveSearch),
        nextState,
        defaultState,
      );
      const to = nextParams.toString();

      // Nothing to change. `setSearchParams` navigates unconditionally, so a write
      // that changes nothing still costs a history entry; skipping those is what
      // keeps Back meaningful, one entry per actual change.
      if (to === effectiveSearch) return;

      pendingUrlWriteRef.current = {
        from: effectiveSearch,
        to,
        state: nextState,
      };
      setSearchParams(nextParams, { replace: options?.replace });
    },
    [searchString, defaultState, setSearchParams],
  );

  // Mirror an explicit `?state=` into session storage so subsequent in-list
  // navigation persists the linked state instead of the stale stored one.
  //
  // Deliberately NOT mount-only: a `?state=` URL can also arrive mid-visit
  // without remounting (the Portus sidebar's global search navigates to
  // `/procedures?state=…`), and the very next path change — opening a row's
  // drawer — drops the query string and falls back to session storage. Syncing
  // only on mount would silently resurrect the pre-navigation state there.
  //
  // This cannot loop: `initialState` is memoized per location, `setStoredState`
  // is identity-stable, and while `?state=` is present the rendered state is
  // read from the URL, never from storage — so writing storage cannot feed back
  // into the URL.
  //
  // Gated on `isReady` so a state the caller has not finished identifying is
  // never written into the settled key's slot; see `TableStateConfig.isReady`.
  useEffect(() => {
    if (!isReady) return;
    if (!hasUrlState) return;
    // A write of ours has not reached the URL yet, so `initialState` is the state
    // that write replaces — see `pendingUrlWriteRef`.
    if (pendingUrlWriteRef.current) return;
    setStoredState(initialState);
  }, [isReady, hasUrlState, initialState, setStoredState]);

  /**
   * ...and the other direction: put a **still-applied** state back into the URL,
   * so coming back to a list whose filters survived in session storage shows them
   * in the address bar instead of silently filtering behind a clean URL.
   *
   * The two conditions are what keep this from being the old mirror:
   *
   * - only when the state differs from `defaultState`, so a pristine list never
   *   grows a `?state=` and this can only ever *add* one — removing it stays a
   *   user action (clearing the filters);
   * - `replace`, so reflecting a filter the user applied earlier costs no history
   *   entry and leaves Back nothing spurious to land on;
   * - and never while a write of our own is in flight, since `state` is then the
   *   state that write replaces (see `pendingUrlWriteRef`).
   *
   * It also cannot write the wrong view's state any more: `state` is read from
   * session storage synchronously for the current key (see `storedForKey`), not
   * from a value that lags the key by a render.
   *
   * No loop: once written, `state` is read back from the URL unchanged, and
   * `writeUrlState` skips writes that would not change the query string.
   *
   * Also gated on `isReady`: "differs from the default" is only a meaningful test
   * once `defaultState` is the real one. On a view URL whose preset is still in
   * flight, every pristine state differs from the preset-less default, so this
   * would stamp a `?state=` onto a list nobody filtered — and the seeding effect
   * above would then adopt it as the view's own.
   */
  useEffect(() => {
    if (!isReady) return;
    // Our own write is already steering the URL, and `state` here is the one it
    // replaces — reflecting it would put the superseded state straight back.
    if (pendingUrlWriteRef.current) return;
    if (isDeepEqual(state, defaultState)) return;
    writeUrlState(state, { replace: true });
  }, [isReady, state, defaultState, writeUrlState]);

  // Memoized state parts
  const pagination = useMemo(() => state.pagination, [state]);
  const sorting = useMemo(() => state.sorting, [state]);
  const filters = useMemo(() => state.filters, [state]);
  const expanded = useMemo(
    () => expandedIdsToRecord(state.expanded),
    [state.expanded],
  );

  // State update helper. An `useEventCallback` so the merge base is the current
  // render's `state` however stale the caller's copy of this function is.
  const setTableState = useEventCallback(
    (
      newState: Partial<TTableState>,
      options?: SearchParamsStateChangeOptions,
    ) => {
      writeUrlState(
        {
          ...(pendingUrlWriteRef.current?.state ?? locationState),
          ...newState,
        },
        options,
      );
      // Merged into the value read synchronously for the CURRENT key, not into
      // `storedState`: that one still holds the PREVIOUS key's value for one
      // render after `storageKey` changes (see `storedForKey` above), and a view
      // switch changes the key without remounting. Merging into it would write
      // `{...previousViewState, ...newState}` into the new view's slot — the very
      // leak `storedForKey` exists to prevent, just on the write path.
      //
      // Defensive: reaching it needs a write committed in the same pass as the key
      // change, which no current caller does and no test can stage (`act()` always
      // flushes the re-read first). Kept because the read and write paths having
      // different merge bases is the kind of thing that only stays benign by luck.
      setStoredState({ ...storedForKeyRef.current, ...newState });
    },
  );

  // Pagination handlers
  const setPagination = useCallback(
    (updater: Updater<TableState["pagination"]>) => {
      const newValue =
        typeof updater === "function" ? updater(pagination) : updater;
      setTableState({ pagination: newValue } as Partial<TTableState>);
    },
    [setTableState, pagination],
  );

  const getPageCount = useCallback(
    (total?: number) => {
      if (typeof total === "undefined") return 0;
      return Math.ceil(total / pagination.pageSize);
    },
    [pagination.pageSize],
  );

  // Sorting handlers
  const setSorting = useCallback(
    (updater: Updater<TableState["sorting"]>) => {
      const newValue =
        typeof updater === "function" ? updater(sorting) : updater;
      setTableState({ sorting: newValue } as Partial<TTableState>);
    },
    [setTableState, sorting],
  );

  /**
   * Expansion handler. Written with `replace`, unlike sorting and filtering:
   * opening a group is a way of *looking* at the list rather than a step
   * through it, and clicking five groups open while hunting for a row would
   * otherwise leave five Back steps that only close them again. Back should
   * leave the list.
   */
  const setExpanded = useCallback<OnChangeFn<ExpandedState>>(
    (updater) => {
      const next = typeof updater === "function" ? updater(expanded) : updater;
      const ids = expandedRecordToIds(next);
      if (!ids) return;

      setTableState({ expanded: ids } as Partial<TTableState>, {
        replace: true,
      });
    },
    [expanded, setTableState],
  );

  // Filter handlers
  const onFiltersChange = useCallback(
    (updatedFilters: Partial<TFilters>) => {
      const newFilters = { ...filters, ...updatedFilters };
      setTableState({
        filters: newFilters,
        pagination: defaultState.pagination, // Reset pagination when filters change
      } as Partial<TTableState>);
    },
    [filters, setTableState, defaultState.pagination],
  );

  const onFiltersClear = useCallback(() => {
    setTableState({
      filters: defaultState.filters,
      pagination: defaultState.pagination,
    } as Partial<TTableState>);
  }, [setTableState, defaultState]);

  return {
    pagination,
    sorting,
    filters,
    expanded,
    setPagination,
    setSorting,
    setExpanded,
    onFiltersChange,
    onFiltersClear,
    getPageCount,
  };
};
